import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CreditCard, Plus, Clock, TrendingUp, X,
  Wallet, Receipt, FileText, Building2, ClipboardList, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import api from '@/lib/axios';
import { formatCurrency, formatDate } from '@/lib/utils';
import { useAuthStore, ROL } from '@/store/auth.store';
import { CorteCajaPage } from './CorteCajaPage';
import { CuentasPorCobrarPage } from './CuentasPorCobrarPage';
import { ProveedoresTab } from './proveedores/ProveedoresTab';
import { PresupuestosTab } from './presupuestos/PresupuestosTab';
import { getErrorMessage } from '@/lib/errors';

/* ── Tab config ── */
type TabId = 'historial' | 'caja' | 'cuentas' | 'fiscal' | 'proveedores' | 'presupuestos';

const TABS: { id: TabId; label: string; icon: React.ElementType; path: string }[] = [
  { id: 'historial',     label: 'Historial',     icon: Receipt,       path: '/finanzas' },
  { id: 'caja',          label: 'Caja',          icon: Wallet,        path: '/finanzas/corte' },
  { id: 'cuentas',       label: 'Por cobrar',    icon: Clock,         path: '/finanzas/cuentas' },
  { id: 'presupuestos',  label: 'Presupuestos',  icon: ClipboardList, path: '/finanzas/presupuestos' },
  { id: 'proveedores',   label: 'Proveedores',   icon: Building2,     path: '/finanzas/proveedores' },
  { id: 'fiscal',        label: 'Fiscal',        icon: FileText,      path: '/finanzas/fiscal' },
];

/* ── Historial tab (existing logic) ── */
const METODO_LABEL: Record<string, string> = {
  efectivo: 'Efectivo', tarjeta: 'Tarjeta', transferencia: 'Transferencia', stripe: 'Stripe',
};
const METODO_COLOR: Record<string, string> = {
  efectivo: 'bg-green-50 text-green-700',
  tarjeta: 'bg-blue-50 text-blue-700',
  transferencia: 'bg-purple-50 text-purple-700',
  stripe: 'bg-indigo-50 text-indigo-700',
};
const ESTADO_COLOR: Record<string, string> = {
  pendiente: 'bg-yellow-100 text-yellow-700',
  pagado: 'bg-green-100 text-green-700',
  reembolsado: 'bg-blue-100 text-blue-600',
  cancelado: 'bg-red-100 text-red-600',
};
const ROLES_REGISTRAR = [ROL.SUPER_ADMIN, ROL.RECEPCIONISTA, ROL.ADMIN_CLINICA, ROL.MEDICO, ROL.MEDICO_ADMIN];
const emptyForm = { pacienteId: '', monto: '', metodo: 'efectivo', referencia: '' };

function HistorialTab() {
  const qc = useQueryClient();
  const { usuario } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const canRegister = usuario?.rolId && ROLES_REGISTRAR.includes(usuario.rolId as any);

  const { data: pagos = [], isLoading } = useQuery({
    queryKey: ['pagos'],
    queryFn: () => api.get('/api/pagos').then(r => r.data),
  });
  const { data: pacientes = [] } = useQuery({
    queryKey: ['pacientes-select'],
    queryFn: () => api.get('/api/pacientes').then(r => r.data),
    enabled: !!canRegister,
  });

  const createMut = useMutation({
    mutationFn: (data: any) => api.post('/api/pagos', data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pagos'] });
      toast.success('Cobro registrado');
      setOpen(false);
      setForm(emptyForm);
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al registrar el cobro')),
  });

  const f = (field: keyof typeof emptyForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm(p => ({ ...p, [field]: e.target.value }));

  const totalCobrado = (pagos as any[]).filter(p => p.estado === 'pagado').reduce((s: number, p: any) => s + Number(p.monto), 0);
  const pendientes = (pagos as any[]).filter(p => p.estado === 'pendiente').length;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-4 md:p-5 flex items-center gap-3 md:gap-4">
          <div className="w-10 h-10 md:w-11 md:h-11 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
            <TrendingUp size={20} className="text-green-600" />
          </div>
          <div>
            <p className="text-xl md:text-2xl font-bold text-text-primary">{formatCurrency(totalCobrado)}</p>
            <p className="text-xs text-text-secondary mt-0.5">Total cobrado</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 md:p-5 flex items-center gap-3 md:gap-4">
          <div className="w-10 h-10 md:w-11 md:h-11 rounded-xl bg-yellow-50 flex items-center justify-center flex-shrink-0">
            <Clock size={20} className="text-yellow-600" />
          </div>
          <div>
            <p className="text-xl md:text-2xl font-bold text-text-primary">{pendientes}</p>
            <p className="text-xs text-text-secondary mt-0.5">Cobro{pendientes !== 1 ? 's' : ''} pendiente{pendientes !== 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-text-secondary">Cargando...</div>
      ) : (pagos as any[]).length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
          <CreditCard size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-text-secondary font-medium">Sin cobros registrados</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 divide-y divide-gray-50">
          {(pagos as any[]).map(p => (
            <div key={p.id} className="flex items-center gap-4 px-4 py-3.5 hover:bg-gray-50 transition-colors">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-text-primary">
                  {p.paciente ? `${p.paciente.nombre} ${p.paciente.apellidoPaterno}` : 'Paciente'}
                </p>
                <p className="text-xs text-text-secondary mt-0.5">{formatDate(p.createdAt)}</p>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${METODO_COLOR[p.metodo] || 'bg-gray-100 text-gray-600'}`}>
                {METODO_LABEL[p.metodo] || p.metodo}
              </span>
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ${ESTADO_COLOR[p.estado] || 'bg-gray-100 text-gray-600'}`}>
                {p.estado}
              </span>
              <p className="font-bold text-text-primary text-sm flex-shrink-0 min-w-[80px] text-right">
                {formatCurrency(p.monto)}
              </p>
            </div>
          ))}
        </div>
      )}

      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />
          <Dialog.Content className="fixed inset-x-0 bottom-0 md:inset-x-auto md:left-1/2 md:top-1/2 md:bottom-auto md:-translate-x-1/2 md:-translate-y-1/2 z-50 bg-white rounded-t-3xl md:rounded-2xl shadow-xl w-full md:max-w-lg p-6 focus:outline-none max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <Dialog.Title className="text-lg font-semibold text-text-primary">Registrar cobro</Dialog.Title>
              <Dialog.Close className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
                <X size={18} />
              </Dialog.Close>
            </div>
            <form onSubmit={e => { e.preventDefault(); createMut.mutate({ ...form, monto: parseFloat(form.monto) }); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Paciente <span className="text-red-500">*</span></label>
                <select required value={form.pacienteId} onChange={f('pacienteId')}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white">
                  <option value="">Seleccionar paciente</option>
                  {(pacientes as any[]).map(p => (
                    <option key={p.id} value={p.id}>{p.nombre} {p.apellidoPaterno}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Monto <span className="text-red-500">*</span></label>
                  <input required type="number" min="0" step="0.01" value={form.monto} onChange={f('monto')} placeholder="0.00"
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Método <span className="text-red-500">*</span></label>
                  <select required value={form.metodo} onChange={f('metodo')}
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white">
                    <option value="efectivo">Efectivo</option>
                    <option value="tarjeta">Tarjeta</option>
                    <option value="transferencia">Transferencia</option>
                    <option value="stripe">Stripe</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Referencia / Folio</label>
                <input value={form.referencia} onChange={f('referencia')} placeholder="Número de transacción o folio"
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
              </div>
              <div className="flex gap-3 pt-2">
                <Dialog.Close className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-text-primary hover:bg-gray-50 transition-colors">
                  Cancelar
                </Dialog.Close>
                <button type="submit" disabled={createMut.isPending}
                  className="flex-1 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60">
                  {createMut.isPending ? 'Guardando...' : 'Registrar cobro'}
                </button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}

/* ── FinanzasPage (hub) ── */
export function FinanzasPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { usuario } = useAuthStore();

  const canRegister = usuario?.rolId && ROLES_REGISTRAR.includes(usuario.rolId as any);

  const activeTab: TabId = (() => {
    const p = location.pathname;
    if (p.includes('/corte')) return 'caja';
    if (p.includes('/cuentas')) return 'cuentas';
    if (p.includes('/proveedores')) return 'proveedores';
    if (p.includes('/presupuestos')) return 'presupuestos';
    if (p.includes('/fiscal')) return 'fiscal';
    return 'historial';
  })();

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-text-primary">Finanzas</h2>
          <p className="text-sm text-text-secondary mt-0.5">Cobros, caja y cuentas por cobrar</p>
        </div>
      </div>

      {/* Mobile select */}
      <div className="md:hidden relative">
        <select
          value={activeTab}
          onChange={e => {
            const found = TABS.find(t => t.id === e.target.value);
            if (found) navigate(found.path);
          }}
          className="w-full appearance-none px-4 py-2.5 pr-10 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-300"
        >
          {TABS.map(tab => <option key={tab.id} value={tab.id}>{tab.label}</option>)}
        </select>
        <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
      </div>
      {/* Desktop pill tabs */}
      <div className="hidden md:block">
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 overflow-x-auto">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const active = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                onClick={() => navigate(tab.path)}
                className={`flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                  active ? 'bg-white text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                <Icon size={13} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'historial' && <HistorialTab />}
      {activeTab === 'caja' && <CorteCajaPage />}
      {activeTab === 'cuentas' && <CuentasPorCobrarPage />}
      {activeTab === 'presupuestos' && <PresupuestosTab />}
      {activeTab === 'proveedores' && <ProveedoresTab />}
      {activeTab === 'fiscal' && (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
          <FileText size={36} className="mx-auto text-gray-300 mb-3" />
          <p className="font-semibold text-text-primary">Facturación fiscal</p>
          <p className="text-sm text-text-secondary mt-1">Próximamente — integración con PAC</p>
        </div>
      )}
    </div>
  );
}
